import { z } from 'zod';
import { PAY_SCHEMES, PAYROLL_RUN_STATUSES } from '@bulk/types';
import { dateOnlySchema, idSchema } from './common';

export const PAYROLL_ADJUSTMENT_KINDS = ['BONUS', 'COMMISSION', 'DEDUCTION', 'ADVANCE'] as const;

export const payrollAdjustmentSchema = z.object({
  kind: z.enum(PAYROLL_ADJUSTMENT_KINDS),
  label: z.string().trim().min(1).max(80),
  amountMinor: z.number().int().min(0).max(100_000_000),
});
export type PayrollAdjustment = z.infer<typeof payrollAdjustmentSchema>;

const segmentSchema = z.object({
  from: z.string(),
  to: z.string(),
  scheme: z.enum(PAY_SCHEMES),
  amountMinor: z.number().int(),
  paidWorkingDays: z.number().int(),
  sessions: z.number().int(),
});

const money = z.number().int();

/** One step of a payroll calculation as data, so a payslip can word it in the reader's language. */
export const payrollStepSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('salary'), amountMinor: money, paidDays: z.number().int(), workingDays: z.number().int(), from: z.string(), to: z.string(), resultMinor: money }),
  z.object({ kind: z.literal('classes'), sessions: z.number().int(), amountMinor: money, from: z.string(), to: z.string(), resultMinor: money }),
  z.object({ kind: z.literal('hours'), hours: z.number(), amountMinor: money, resultMinor: money }),
  z.object({ kind: z.literal('adjustment'), adjustment: z.enum(PAYROLL_ADJUSTMENT_KINDS), label: z.string(), amountMinor: money }),
  z.object({ kind: z.literal('gross'), resultMinor: money }),
  z.object({ kind: z.literal('pension_employee'), rateBps: z.number().int(), resultMinor: money }),
  z.object({ kind: z.literal('income_tax'), rateBps: z.number().int(), baseMinor: money, resultMinor: money }),
  z.object({ kind: z.literal('net'), resultMinor: money }),
  z.object({ kind: z.literal('pension_employer'), rateBps: z.number().int(), resultMinor: money }),
  z.object({ kind: z.literal('employer_cost'), resultMinor: money }),
]);
export type PayrollStep = z.infer<typeof payrollStepSchema>;

export const payrollLineSchema = z.object({
  id: idSchema,
  runId: idSchema,
  employeeId: idSchema,
  employeeName: z.string(),
  position: z.string().nullable(),
  departmentName: z.string().nullable(),
  personalId: z.string().nullable(),
  bankIban: z.string().nullable(),
  scheme: z.enum(PAY_SCHEMES),
  rateMinor: z.number().int(),
  pensionParticipant: z.boolean(),
  segments: z.array(segmentSchema),
  workingDaysInPeriod: z.number().int(),
  paidWorkingDays: z.number().int(),
  unpaidLeaveDays: z.number().int(),
  paidLeaveDays: z.number().int(),
  sessionsTaught: z.number().int(),
  hours: z.number(),
  hoursFromTimesheet: z.boolean().describe('The hours are the approved timesheet entries of the period; off once the accountant types hours'),
  adjustments: z.array(payrollAdjustmentSchema),
  baseMinor: z.number().int(),
  additionsMinor: z.number().int(),
  deductionsMinor: z.number().int(),
  grossMinor: z.number().int(),
  pensionEmployeeMinor: z.number().int(),
  taxableMinor: z.number().int(),
  incomeTaxMinor: z.number().int(),
  netMinor: z.number().int(),
  pensionEmployerMinor: z.number().int(),
  employerCostMinor: z.number().int(),
  breakdown: z.array(z.string()),
  steps: z.array(payrollStepSchema).describe('The breakdown as data; empty for a line calculated before steps were stored'),
});
export type PayrollLineDto = z.infer<typeof payrollLineSchema>;

export const payrollRunSchema = z.object({
  id: idSchema,
  period: z.string(),
  periodStart: z.string(),
  periodEnd: z.string(),
  status: z.enum(PAYROLL_RUN_STATUSES),
  currency: z.string(),
  headcount: z.number().int(),
  grossMinor: z.number().int(),
  netMinor: z.number().int(),
  incomeTaxMinor: z.number().int(),
  pensionEmployeeMinor: z.number().int(),
  pensionEmployerMinor: z.number().int(),
  deductionsMinor: z.number().int(),
  employerCostMinor: z.number().int(),
  rates: z.object({ incomeTaxBps: z.number().int(), pensionEmployeeBps: z.number().int(), pensionEmployerBps: z.number().int(), pensionReducesTaxableBase: z.boolean() }),
  calculatedAt: z.string().nullable(),
  approvedByName: z.string().nullable(),
  approvedAt: z.string().nullable(),
  paidAt: z.string().nullable(),
  paidNote: z.string().nullable(),
  reopenReason: z.string().nullable(),
  monthClosed: z.boolean().describe('The finance month is closed: the run can still be approved and marked paid, nothing else changes'),
  createdAt: z.string(),
});
export type PayrollRunDto = z.infer<typeof payrollRunSchema>;

export const payrollRunDetailSchema = payrollRunSchema.extend({ lines: z.array(payrollLineSchema) });
export type PayrollRunDetailDto = z.infer<typeof payrollRunDetailSchema>;

export const payrollPeriodSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Expected YYYY-MM');

export const createPayrollRunSchema = z.object({ period: payrollPeriodSchema });
export type CreatePayrollRunInput = z.infer<typeof createPayrollRunSchema>;

export const updatePayrollLineSchema = z.object({
  hours: z.number().min(0).max(744).multipleOf(0.01).optional().describe('Hours worked in the period, for hourly pay; typing them stops the line taking hours from the timesheet'),
  useTimesheet: z.literal(true).optional().describe('Go back to the approved timesheet hours'),
  adjustments: z.array(payrollAdjustmentSchema).max(20).optional(),
});
export type UpdatePayrollLineInput = z.infer<typeof updatePayrollLineSchema>;

export const addPayrollLineSchema = z.object({ employeeId: idSchema });

export const reopenPayrollRunSchema = z.object({ reason: z.string().trim().min(3).max(500) });
export const markPayrollPaidSchema = z.object({ paidAt: dateOnlySchema, note: z.string().trim().max(500).nullable().optional() });
export type MarkPayrollPaidInput = z.infer<typeof markPayrollPaidSchema>;

export const myPayslipSchema = payrollLineSchema.extend({
  period: z.string(),
  status: z.enum(PAYROLL_RUN_STATUSES),
  paidAt: z.string().nullable(),
  currency: z.string(),
});
export type MyPayslipDto = z.infer<typeof myPayslipSchema>;

export const payslipLocaleQuerySchema = z.object({ locale: z.enum(['en', 'ka']).default('en') });

export const payslipSchema = z.object({
  lineId: idSchema,
  runId: idSchema,
  period: z.string(),
  status: z.enum(PAYROLL_RUN_STATUSES),
  currency: z.string(),
  grossMinor: z.number().int(),
  netMinor: z.number().int(),
  paidAt: z.string().nullable(),
});
export type PayslipDto = z.infer<typeof payslipSchema>;
